<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-between">
      <div class="actions-left">
        <div class="btn-group" role="group">
          <button 
            type="button" 
            class="btn" 
            :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'list'"
          >
            <i class="fas fa-list"></i> {{ $t('list') }}
          </button>
          <button 
            type="button" 
            class="btn" 
            :class="viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'calendar'"
          >
            <i class="fas fa-calendar"></i> {{ $t('calendar') }}
          </button>
        </div>
      </div>
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="addGuide">{{ $t('add') }}</button>
      </div>
    </div>
    
    <!-- List View -->
    <div v-if="viewMode === 'list'">
      <SearchBar @search-clicked="searchClicked"></SearchBar>
      <CTableWrapper 
        ref="table" 
        hover 
        striped 
        :caption="$t('guides')" 
        :items="items" 
        :actions="actions" 
        :pages="pages" 
        :fields="fields" 
        clickable-rows 
        @row-clicked="rowClicked" 
        @action-clicked="actionClicked" 
        @update-requested="updateItemsRequested" 
        :sorter="{external: true}" 
        :sorterValue="sorterValue" 
        @sorter-value-changed="sorterValueChanged" 
        :loading="loading"
      >
      </CTableWrapper>
    </div>
    
    <!-- Calendar View -->
    <div v-else-if="viewMode === 'calendar'" class="calendar-view">
      <div class="calendar-header">
        <h4>{{ $t('guides.calendar') }}</h4>
        <div class="calendar-controls">
          <button @click="previousMonth" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="current-month">{{ currentMonthYear }}</span>
          <button @click="nextMonth" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-chevron-right"></i>
          </button>
          <button @click="resetCalendar" class="btn btn-outline-info btn-sm ml-2" title="Reset to current month">
            <i class="fas fa-home"></i>
          </button>
        </div>
      </div>
      <div class="calendar-legend">
        <div class="legend-item">
          <span class="legend-color status-pending"></span>
          <span class="legend-label">{{ $t('reservations.status.pending') }}</span>
        </div>
        <div class="legend-item">
          <span class="legend-color status-confirmed"></span>
          <span class="legend-label">{{ $t('reservations.status.confirmed') }}</span>
        </div>
        <div class="legend-item">
          <span class="legend-color status-cancelled"></span>
          <span class="legend-label">{{ $t('reservations.status.cancelled') }}</span>
        </div>
        <div class="legend-item">
          <span class="legend-color status-completed"></span>
          <span class="legend-label">{{ $t('reservations.status.completed') }}</span>
        </div>
      </div>
      <div class="calendar-grid">
        <div v-for="day in calendarDays" :key="day.date" class="calendar-day" :class="{ 'other-month': !day.currentMonth, 'today': day.isToday }">
          <div class="day-number">{{ day.day }}</div>
          <div class="day-reservations">
            <div v-for="reservation in day.reservations" :key="reservation.id" class="reservation-item" :class="`status-${reservation.status.toLowerCase()}`" @click="viewReservation(reservation)">
              <span class="reservation-time">{{ reservation.time }}</span>
              <span class="reservation-guide">{{ reservation.guideName }}</span>
              <span class="reservation-experience">{{ reservation.experience }}</span>
              <span class="reservation-people">{{ reservation.people }} {{ $t('reservations.people') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de confirmação de exclusão -->
    <CModal
      :show.sync="showDeleteModal"
      :title="$t('confirmDelete')"
      color="primary"
    >
      <p>{{ $t('confirmRemove', { name: guideToDelete && guideToDelete.name ? guideToDelete.name : '' }) }}</p>
      <template #footer>
        <CButton @click="showDeleteModal = false" color="secondary">{{ $t('cancel') }}</CButton>
        <CButton @click="confirmDelete" color="primary" :disabled="deleting">
          <CSpinner v-if="deleting" size="sm" class="mr-1"></CSpinner>
          {{ $t('delete') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import SearchBar from "@/controls/SearchBar.vue";
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";

export default {
  components: { CTableWrapper, SearchBar },
  data () {
    return {
      viewMode: 'list', // 'list' or 'calendar'
      searchText: '',
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      currentDate: (() => {
        const today = new Date();
        const date = new Date(today.getFullYear(), today.getMonth(), 1);
        date.setHours(0, 0, 0, 0);
        return date;
      })(),
      fields: [
        { key: 'name', label: i18n.t('name'), _classes: 'font-weight-bold', sortable: true },
        { key: 'email', label: i18n.t('email'), sortable: true },
        { key: 'phone', label: i18n.t('phone'), sortable: true },
        { key: 'isActive', label: i18n.t('guides.active'), sortable: true },
      ],
      actions: [
        { key: 'view', label: i18n.t('view') },
        { key: 'remove', label: i18n.t('remove') }
      ],
      sorterValue: { column: 'name', asc: true },
      loading: false,
      showDeleteModal: false,
      guideToDelete: null,
      deleting: false
    }
  },
  computed: {
    ...mapState('guides', ['getGuidesOutput']),
    ...mapState('reservations', ['getReservationsOutput']),
    ...mapState('experiences', ['getExperiencesOutput']),
    guidesInfo: function() {
      return this.getGuidesOutput ? this.getGuidesOutput : null;
    },
    reservationsInfo: function() {
      return this.getReservationsOutput ? this.getReservationsOutput : null;
    },
    experienceIdToName() {
      const list = (this.getExperiencesOutput && this.getExperiencesOutput.data) ? this.getExperiencesOutput.data : [];
      const map = new Map();
      list.forEach(e => {
        if (e && e.id != null) {
          map.set(e.id, e.name || '');
        }
      });
      return map;
    },
    items: function()
    {
      return this.guidesInfo && this.guidesInfo.data ? this.guidesInfo.data : [];
    },
    pages: function()
    {
      return this.guidesInfo ? this.guidesInfo.totalPages : 1;
    },
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    calendarDays() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Verificar se a data atual é válida
        if (isNaN(this.currentDate.getTime())) {
          console.error('Invalid currentDate:', this.currentDate);
          return [];
        }
        
        const firstDay = new Date(year, month, 1);
        const startDate = new Date(year, month, 1 - firstDay.getDay());
        
        const days = [];
        const seenDates = new Set(); // Para evitar duplicatas
        const current = new Date(startDate);
        
        // Gerar exatamente 42 dias (6 semanas)
        for (let i = 0; i < 42; i++) {
          // Verificar se a data é válida antes de processar
          if (isNaN(current.getTime())) {
            console.error('Invalid date in calendar loop:', current);
            break;
          }
          
          // Formatar data localmente sem problemas de timezone
          const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
          
          // Verificar se já existe essa data (evitar duplicatas)
          if (seenDates.has(dateStr)) {
            console.warn('Duplicate date detected:', dateStr);
            current.setDate(current.getDate() + 1);
            continue;
          }
          
          seenDates.add(dateStr);
          
          const isCurrentMonth = current.getMonth() === month;
          const currentDateOnly = new Date(current);
          currentDateOnly.setHours(0, 0, 0, 0);
          const isToday = currentDateOnly.getTime() === today.getTime();
          
          days.push({
            date: dateStr,
            day: current.getDate(),
            currentMonth: isCurrentMonth,
            isToday: isToday,
            reservations: this.getReservationsForDate(current)
          });
          
          // Avançar para o próximo dia de forma segura
          const nextDate = new Date(current);
          nextDate.setDate(nextDate.getDate() + 1);
          current.setTime(nextDate.getTime());
        }
        
        return days;
      } catch (error) {
        console.error('Error in calendarDays:', error);
        return [];
      }
    }
  },
  mounted: async function()
  {
    // Load experiences first for name lookup
    try {
      await this.getExperiences({ 
        pageSize: 100, 
        pageNumber: 1,
        propertyName: 'Position',
        orderBy: 1
      });
    } catch (error) {
      console.error('Error loading experiences on mount:', error);
    }
    
    this.updateItemsRequested(1, this.itemsPerPage);
    
    // Load reservations for calendar view
    if (this.viewMode === 'calendar') {
      this.loadReservationsForCalendar();
    }
  },
  methods: {
    ...mapActions('guides', ['getGuides']),
    ...mapActions('guides', ['deleteGuideById']),
    ...mapActions('reservations', ['getReservations']),
    ...mapActions('experiences', ['getExperiences']),
    rowClicked (item, index) {
      this.$router.push({name: "GuidesEdit", query: { id: item.id }, params : { name: item.name } });
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.$router.push({name: "GuidesEdit", query: { id: item.id }, params : { name: item.name } });
          break;
        }
        case 'remove':
        {
          this.openDeleteModal(item);
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, this.searchText, this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));

      this.getGuides(input); 
    },
    addGuide () 
    {
      router.push({ name: 'GuidesAdd'});
    },
    editGuide(guide) {
      this.$router.push({name: "GuidesEdit", query: { id: guide.id }, params : { name: guide.name } });
    },
    searchClicked(searchText)
    {
      this.searchText = searchText;
      this.$refs.table.reset();

      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    previousMonth() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Criar nova data de forma segura
        const newDate = new Date(year, month - 1, 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Verificar se a data é válida
        if (isNaN(newDate.getTime())) {
          console.error('Invalid date created:', { year, month: month - 1 });
          return;
        }
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
        
        // Reload reservations for new month
        this.loadReservationsForCalendar();
      } catch (error) {
        console.error('Error in previousMonth:', error);
      }
    },
    nextMonth() {
      try {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Criar nova data de forma segura
        const newDate = new Date(year, month + 1, 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Verificar se a data é válida
        if (isNaN(newDate.getTime())) {
          console.error('Invalid date created:', { year, month: month + 1 });
          return;
        }
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
        
        // Reload reservations for new month
        this.loadReservationsForCalendar();
      } catch (error) {
        console.error('Error in nextMonth:', error);
      }
    },
    resetCalendar() {
      try {
        const today = new Date();
        const newDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newDate.setHours(0, 0, 0, 0);
        
        // Atualizar usando Vue.set para garantir reatividade
        this.$set(this, 'currentDate', newDate);
        
        // Reload reservations
        this.loadReservationsForCalendar();
      } catch (error) {
        console.error('Error resetting calendar:', error);
      }
    },
    loadReservationsForCalendar() {
      // Load all reservations for the current month view
      // We'll load a larger page size to get all reservations for the month
      const input = new InputGetList(1000, 1, '', 1, 'ReservationDate');
      this.getReservations(input);
    },
    getReservationsForDate(date) {
      try {
        // Verificar se a data é válida
        if (!date || isNaN(date.getTime())) {
          console.error('Invalid date in getReservationsForDate:', date);
          return [];
        }
        
        // Formatar data localmente sem problemas de timezone
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        const raw = this.reservationsInfo && this.reservationsInfo.data ? this.reservationsInfo.data : [];
        
        return raw
          .filter(r => {
            try {
              const availability = r.experienceAvailability || null;
              const reservationDate = r.reservationDate || (availability && availability.availableDate);
              if (!reservationDate) return false;
              
              // Extrair apenas a parte da data (YYYY-MM-DD) da string
              const reservationDateStr = reservationDate.split('T')[0];
              return reservationDateStr === dateStr;
            } catch (error) {
              console.error('Error filtering reservation:', error, r);
              return false;
            }
          })
          .map(r => {
            try {
              const availability = r.experienceAvailability || null;
              const start = availability && availability.startTime ? availability.startTime.toString().substring(0,5) : '';
              let experienceName = (r.experience && r.experience.name) ? r.experience.name : '';
              if (!experienceName && r.experienceId) {
                experienceName = this.experienceIdToName.get(r.experienceId) || `#${r.experienceId}`;
              }
              
              // Get guide name from availability
              let guideName = '';
              if (availability && availability.guide) {
                guideName = availability.guide.name || '';
              } else if (availability && availability.guideId) {
                // If guide object is not loaded, try to get from guides list
                const guides = this.guidesInfo && this.guidesInfo.data ? this.guidesInfo.data : [];
                const guide = guides.find(g => g.id === availability.guideId);
                guideName = guide ? guide.name : '';
              }
              
              // Count people from multiple possible sources
              let people = 0;
              if (r.totalPeople && typeof r.totalPeople === 'number') {
                people = r.totalPeople;
              } else if (r.numberOfPeople && typeof r.numberOfPeople === 'number') {
                people = r.numberOfPeople;
              } else if (Array.isArray(r.people)) {
                people = r.people.length;
              } else if (r.reservationPeople && Array.isArray(r.reservationPeople)) {
                people = r.reservationPeople.length;
              } else if (r.people && typeof r.people === 'number') {
                people = r.people;
              }
              
              return {
                id: r.id,
                time: availability ? start : '',
                guideName: guideName,
                experience: experienceName,
                people: people,
                status: r.status,
                touristName: r.touristName
              };
            } catch (error) {
              console.error('Error mapping reservation:', error, r);
              return null;
            }
          })
          .filter(r => r !== null && r.guideName); // Only show reservations with guides
      } catch (error) {
        console.error('Error in getReservationsForDate:', error);
        return [];
      }
    },
    viewReservation(reservation) {
      this.$router.push({
        name: "ReservationsEdit", 
        query: { id: reservation.id }, 
        params : { 
          name: reservation.touristName,
          time: reservation.time,
          totalPeople: reservation.people
        } 
      });
    },
    openDeleteModal(item) {
      this.guideToDelete = item;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      if (!this.guideToDelete) return;
      
      try {
        this.deleting = true;
        await this.deleteGuideById(this.guideToDelete.id);
        
        // Success - close modal and refresh list
        this.showDeleteModal = false;
        this.guideToDelete = null;
        this.deleting = false;
        
        // Refresh the list
        this.updateItemsRequested(1, this.itemsPerPage);
        
        // Show success message
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('guides.deleteSuccess')
        });
      } catch (error) {
        console.error('Error deleting guide:', error);
        this.deleting = false;
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('guides.deleteError')
        });
      }
    }
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    viewMode: function(newMode) {
      if (newMode === 'calendar') {
        this.loadReservationsForCalendar();
      }
    },
    getGuidesOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    },
    getReservationsOutput: function(val)
    {
      if(val != null && this.viewMode === 'calendar')
      {
        // Reservations loaded, calendar will update automatically
      }
    }
  }
}
</script>

<style scoped>
.actions-left {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  margin-right: 0;
}

.calendar-view {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.calendar-legend {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  display: inline-block;
}

.legend-color.status-pending {
  background: #ffc107;
}

.legend-color.status-confirmed {
  background: #28a745;
}

.legend-color.status-cancelled {
  background: #dc3545;
}

.legend-color.status-completed {
  background: #6c757d;
}

.legend-label {
  font-size: 0.9rem;
  color: #333;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.current-month {
  min-width: 180px;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-day {
  background: white;
  min-height: 120px;
  padding: 0.5rem;
  position: relative;
}

.calendar-day.other-month {
  background: #f8f9fa;
  color: #6c757d;
}

.calendar-day.today {
  background: #e3f2fd;
}

.day-number {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.day-reservations {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reservation-item {
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
  color: white;
}

.reservation-item.status-pending {
  background: #ffc107;
}

.reservation-item.status-confirmed {
  background: #28a745;
}

.reservation-item.status-cancelled {
  background: #dc3545;
}

.reservation-item.status-completed {
  background: #6c757d;
}

.reservation-item:hover {
  opacity: 0.8;
}

.reservation-time {
  display: block;
  font-weight: bold;
}

.reservation-guide {
  display: block;
  font-weight: bold;
  font-size: 0.75rem;
}

.reservation-experience {
  display: block;
  font-size: 0.7rem;
  opacity: 0.9;
}

.reservation-people {
  display: block;
  font-size: 0.7rem;
  opacity: 0.9;
}

@media (max-width: 768px) {
  .calendar-grid {
    font-size: 0.8rem;
  }
  
  .calendar-day {
    min-height: 80px;
    padding: 0.25rem;
  }
  
  .reservation-item {
    font-size: 0.7rem;
  }
}
</style>

